import moment from 'moment';
import get from 'lodash/get';
import { isIE, isIOS } from 'mobile-device-detect';
import { isNativeApp } from './platform-helper';
import FileSystemNativeService from '@/mobile/file-system-native.service';
import { DEFAULT_DATE_FORMAT } from '@/models/defaults';
import { logger } from '@/helpers/log.service';

export function base64ToArrayBuffer(base64: string) {
  let raw = atob(base64);
  return Uint8Array.from(
    Array.prototype.map.call(raw, (x) => { return x.charCodeAt(0); }) as Array<number>
  );
}

export function arrayBufferToBase64(buf: Uint8Array) {
  const bufStr = Array.prototype.map.call(buf, (ch) => {
    return String.fromCharCode(ch);
  }).join('');
  return btoa(bufStr);
}

export function downloadFile(base64: string, fileName: string) {
  if (base64) {
    let blob = new Blob([base64ToArrayBuffer(base64)]);
    _downloadFile(blob, fileName);
  }
}

export function downloadFileBlob(fileBlob: Blob, fileName: string) {
  let blob = new Blob([fileBlob]);
  _downloadFile(blob, fileName);
}

export function getFileNameFromResponse(response: any) {
  let contentDisposition = get(response, 'headers.content-disposition');
  let fn;
  if (contentDisposition) {
    let splitter = /filename="/.test(contentDisposition) ? 'filename="' : /filename=/.test(contentDisposition) ? 'filename=' : '';
    if (splitter) {
      let arr = contentDisposition.split(splitter);
      if (arr.length) {
        fn = arr[1].split(splitter === 'filename="' ? '";' : ';')[0];
      }
    }
  }
  return fn ? base64Decode(fn) : fn;
}

export function base64Decode(base64: string): string {
  let buf = base64ToArrayBuffer(base64);
  return Buffer.from(buf.buffer).toString();
}

function _downloadFile(fileBlob: Blob, fileName: string) {
  if (isIE) {
    window.navigator.msSaveOrOpenBlob(fileBlob, fileName);
  } else {
    let url = window.URL.createObjectURL(fileBlob);
    if (isNativeApp()) {
      FileSystemNativeService.saveFile(fileName, fileBlob);
    } else {
      let a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  }
}

export function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function isProduction(): boolean {
  return process.env.NODE_ENV === 'production';
}

export function isDevelopment(): boolean {
  return process.env.NODE_ENV === 'development';
}

export function urlParam(name) {
  let results = new RegExp('[\\?&]' + name + '=([^&#]*)').exec(window.location.href);
  if (results == null) {
    return null;
  } else {
    return decodeURI(results[1]) || 0;
  }
}

export function formatDate(value, format) {
  if (value) {
    let v = String(value);
    if (isIOS &&
        /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)((-(\d{2}):(\d{2})|Z)?)$/.test(v)) {
      // https://stackoverflow.com/a/13363791/9294219
      v = v.replace('T', ' ').replace(/-/g, '/');
    }
    let d = new Date(v);
    return moment(d).format(format || DEFAULT_DATE_FORMAT);
  }
  return value;
}

export function deepClone(obj: any): any {
  return obj !== undefined ? JSON.parse(JSON.stringify(obj)) : obj;
}

export function convertHexToRgba(hexCode: string, opacity = 1): string {
  let hex = hexCode.replace('#', '');

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100;
  }

  return `rgba(${r},${g},${b},${opacity})`;
}

export function decodeHTMLEntities(text) {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
}

export function encodeHTMLEntities(text) {
  const textArea = document.createElement('textarea');
  textArea.innerText = text;
  return textArea.innerHTML;
}

export async function copyToClipboard(text: string): Promise<void> {
  if (typeof navigator.clipboard !== 'undefined' && typeof navigator.clipboard.writeText === 'function' && !isNativeApp()) {
    await navigator.clipboard.writeText(text);
  } else {
    let textInput: HTMLInputElement | undefined;

    try {
      textInput = createCopyToClipboardEl(text);

      selectTextInCopyToClipboardEl(textInput);

      document.execCommand('copy');
    } catch (e) {
      logger.error(e);
    } finally {
      removeCopyToClipboardEl(textInput);
    }
  }
}

const copyToClipboardInputElId = 'copyToClipboardInput';

function createCopyToClipboardEl(text: string): HTMLInputElement {
  let textInput = document.getElementById(copyToClipboardInputElId) as HTMLInputElement;

  if (!textInput) {
    textInput = document.createElement('input') as HTMLInputElement;
    textInput.id = copyToClipboardInputElId;
  }
  textInput.value = text;

  document.body.appendChild(textInput);

  return textInput;
}

function selectTextInCopyToClipboardEl(el: HTMLInputElement): void {
  if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
    el.contentEditable = 'true';
    el.readOnly = false;

    let range = document.createRange();
    range.selectNodeContents(el);
    let selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    el.setSelectionRange(0, 999999);
  } else {
    el.select();
  }
}

function removeCopyToClipboardEl(el?: HTMLInputElement): void {
  try {
    if (el) {
      document.body.removeChild(el);
    }
  } catch {
    // ignored
  }
}
